import React, { useState, memo, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, ChevronRight } from 'lucide-react';
import { MultiSelectDropdown } from './ui/MultiSelectDropdown';
import { SingleSelectDropdown } from './ui/SingleSelectDropdown';
interface SurveyData {
  // Welcome
  email: string;
  // Your Background
  primaryRole: string;
  primaryRoleOther: string;
  yearsWorking: string;
  coachingTraining: string;
  founderExperience: string;
  soloOrMulti: string;
  geographicLocation: string;
  workRemotely: string;
  // Client Profile
  totalFoundingTeams: string;
  typicalFounderStructure: string;
  commonTeamSize: string;
  fundingStages: string[];
  industries: string[];
  industriesOther: string;
  howFoundersFind: string[];
  howFoundersFindOther: string;
  founderPreference: string;
  // Solo Founder Challenges
  soloFounderChallenges: string[];
  soloFounderChallengesOther: string;
  topSoloChallenge: string;
  topSoloChallengeOther: string;
  soloFounderTriggers: string[];
  soloFounderTriggersOther: string;
  soloFounderStage: string;
  // Multi-Founder Challenges
  multiFounderChallenges: string[];
  multiFounderChallengesOther: string;
  topMultiChallenge: string;
  topMultiChallengeOther: string;
  cofounderConflictStage: string;
  cofounderConflictTriggers: string[];
  cofounderConflictTriggersOther: string;
  // Comparing Solo vs Multi
  greaterChallenges: string;
  soloMorePronounced: string[];
  soloMorePronouncedOther: string;
  multiMorePronounced: string[];
  multiMorePronouncedOther: string;
  higherSuccessRate: string;
  easierToCoach: string;
  easierHarderExplanation: string;
  // Cofounder Relationships
  cofounderRelationshipFrequency: string;
  cofounderIssues: string[];
  cofounderIssuesOther: string;
  relationshipBreakdownFrequency: string;
  relationshipFailureOutcome: string;
  relationshipFailureOutcomeOther: string;
  effectiveInterventions: string[];
  effectiveInterventionsOther: string;
  // Cofounder Selection
  cofounderSelectionImportance: number;
  cofounderMistakes: string[];
  cofounderMistakesOther: string;
  cofounderPriorities: string[];
  cofounderRedFlags: string[];
  cofounderRedFlagsOther: string;
  // Team Composition
  idealTeamSize: string;
  bestCombinations: string[];
  bestCombinationsOther: string;
  strugglingCombinations: string[];
  strugglingCombinationsOther: string;
  workedTogetherImportance: number;
  complementarySkillsImportance: number;
  alignedValuesImportance: number;
  // Decision-Making & Governance
  bestDecisionStructures: string[];
  bestDecisionStructuresOther: string;
  decisionBreakdownCauses: string[];
  decisionBreakdownCausesOther: string;
  soloVsMultiDecisions: string;
  // Equity & Compensation
  equityIssuesFrequency: string;
  commonEquitySplits: string;
  recommendedEquitySplit: string;
  equityConflictIssues: string[];
  equityConflictIssuesOther: string;
  recommendCofounderAgreements: string;
  // Founder Transitions
  soloAddCofounderFrequency: string;
  soloSeekCofounderReasons: string[];
  soloSeekCofounderReasonsOther: string;
  lateCofounderSuccessRate: string;
  lateCofounderSuccessFactors: string;
  cofounderDepartureFrequency: string;
  survivalFactors: string[];
  survivalFactorsOther: string;
  // Your Coaching Approach
  differentApproach: string;
  soloFounderDifferences: string[];
  soloFounderDifferencesOther: string;
  whoYouWorkWith: string;
  conflictingPerspectives: string[];
  conflictingPerspectivesOther: string;
  coachingStyle: string;
  coachingStyleOther: string;
  engagementStructure: string;
  meetingFrequency: string;
  formats: string[];
  formatsOther: string;
  toolsFrameworks: string[];
  toolsFrameworksOther: string;
  // Effectiveness & Outcomes
  founderPreparedness: number;
  successMeasures: string[];
  successMeasuresOther: string;
  averageEngagementLength: string;
  disengagementReason: string;
  disengagementReasonOther: string;
  // Investor Perspectives
  investorPreference: string;
  investorPreferredTeamSize: string;
  investorSoloConcerns: string[];
  investorSoloConcernsOther: string;
  investorMultiConcerns: string[];
  investorMultiConcernsOther: string;
  investorBiasFrequency: string;
  navigatingBias: string[];
  navigatingBiasOther: string;
  advisedAddCofounder: string;
  addCofounderFactors: string[];
  addCofounderFactorsOther: string;
  // Ecosystem Gaps
  resourceEffectiveness: number;
  soloSupportGaps: string[];
  soloSupportGapsOther: string;
  multiSupportGaps: string[];
  multiSupportGapsOther: string;
  valuableResources: string[];
  valuableResourcesOther: string;
  useAssessmentTool: string;
  toolFeatures: string[];
  toolFeaturesOther: string;
  // Business Model
  compensationModel: string[];
  compensationModelOther: string;
  rateRange: string;
  reducedRates: string;
  // Final Reflections
  biggestFrustration: string;
  biggestReward: string;
  biggestSuccessFactor: string;
  wishFoundersUnderstood: string;
  successStory: string;
  cautionaryTale: string;
  adviceToCoaches: string;
  anythingElse: string;
  // Closing
  openToFollowUp: string;
}
const SECTIONS = [{
  id: 'welcome',
  label: 'Welcome',
  required: false
}, {
  id: 'background',
  label: 'Your Background',
  required: true
}, {
  id: 'clients',
  label: 'Client Profile',
  required: true
}, {
  id: 'solo',
  label: 'Solo Founder Challenges',
  required: false
}, {
  id: 'multi',
  label: 'Multi-Founder Challenges',
  required: false
}, {
  id: 'comparing',
  label: 'Comparing',
  required: false
}, {
  id: 'relationships',
  label: 'Cofounder Relationships',
  required: false
}, {
  id: 'selection',
  label: 'Cofounder Selection',
  required: false
}, {
  id: 'composition',
  label: 'Team Composition',
  required: false
}, {
  id: 'decisions',
  label: 'Decision-Making',
  required: false
}, {
  id: 'equity',
  label: 'Equity',
  required: false
}, {
  id: 'transitions',
  label: 'Transitions',
  required: false
}, {
  id: 'approach',
  label: 'Your Approach',
  required: true
}, {
  id: 'outcomes',
  label: 'Outcomes',
  required: false
}, {
  id: 'investors',
  label: 'Investors',
  required: false
}, {
  id: 'gaps',
  label: 'Ecosystem Gaps',
  required: false
}, {
  id: 'business',
  label: 'Business Model',
  required: false
}, {
  id: 'reflections',
  label: 'Final Reflections',
  required: true
}, {
  id: 'closing',
  label: 'Closing',
  required: false
}];
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzNjDQ1likGiUH3i0A7EzxBaGSBbIOnPL-xxyPGSz4_YbnL9cqkxQzgnBFbDlVwXvGTgg/exec';
export function MentorAdvisorSurvey({
  onClose
}: {
  onClose: () => void;
}) {
  const [currentSection, setCurrentSection] = useState('welcome');
  const [completedSections, setCompletedSections] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [sessionId] = useState(() => `mentor_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<SurveyData>({
    email: '',
    primaryRole: '',
    primaryRoleOther: '',
    yearsWorking: '',
    coachingTraining: '',
    founderExperience: '',
    soloOrMulti: '',
    geographicLocation: '',
    workRemotely: '',
    totalFoundingTeams: '',
    typicalFounderStructure: '',
    commonTeamSize: '',
    fundingStages: [],
    industries: [],
    industriesOther: '',
    howFoundersFind: [],
    howFoundersFindOther: '',
    founderPreference: '',
    soloFounderChallenges: [],
    soloFounderChallengesOther: '',
    topSoloChallenge: '',
    topSoloChallengeOther: '',
    soloFounderTriggers: [],
    soloFounderTriggersOther: '',
    soloFounderStage: '',
    multiFounderChallenges: [],
    multiFounderChallengesOther: '',
    topMultiChallenge: '',
    topMultiChallengeOther: '',
    cofounderConflictStage: '',
    cofounderConflictTriggers: [],
    cofounderConflictTriggersOther: '',
    greaterChallenges: '',
    soloMorePronounced: [],
    soloMorePronouncedOther: '',
    multiMorePronounced: [],
    multiMorePronouncedOther: '',
    higherSuccessRate: '',
    easierToCoach: '',
    easierHarderExplanation: '',
    cofounderRelationshipFrequency: '',
    cofounderIssues: [],
    cofounderIssuesOther: '',
    relationshipBreakdownFrequency: '',
    relationshipFailureOutcome: '',
    relationshipFailureOutcomeOther: '',
    effectiveInterventions: [],
    effectiveInterventionsOther: '',
    cofounderSelectionImportance: 0,
    cofounderMistakes: [],
    cofounderMistakesOther: '',
    cofounderPriorities: [],
    cofounderRedFlags: [],
    cofounderRedFlagsOther: '',
    idealTeamSize: '',
    bestCombinations: [],
    bestCombinationsOther: '',
    strugglingCombinations: [],
    strugglingCombinationsOther: '',
    workedTogetherImportance: 0,
    complementarySkillsImportance: 0,
    alignedValuesImportance: 0,
    bestDecisionStructures: [],
    bestDecisionStructuresOther: '',
    decisionBreakdownCauses: [],
    decisionBreakdownCausesOther: '',
    soloVsMultiDecisions: '',
    equityIssuesFrequency: '',
    commonEquitySplits: '',
    recommendedEquitySplit: '',
    equityConflictIssues: [],
    equityConflictIssuesOther: '',
    recommendCofounderAgreements: '',
    soloAddCofounderFrequency: '',
    soloSeekCofounderReasons: [],
    soloSeekCofounderReasonsOther: '',
    lateCofounderSuccessRate: '',
    lateCofounderSuccessFactors: '',
    cofounderDepartureFrequency: '',
    survivalFactors: [],
    survivalFactorsOther: '',
    differentApproach: '',
    soloFounderDifferences: [],
    soloFounderDifferencesOther: '',
    whoYouWorkWith: '',
    conflictingPerspectives: [],
    conflictingPerspectivesOther: '',
    coachingStyle: '',
    coachingStyleOther: '',
    engagementStructure: '',
    meetingFrequency: '',
    formats: [],
    formatsOther: '',
    toolsFrameworks: [],
    toolsFrameworksOther: '',
    founderPreparedness: 0,
    successMeasures: [],
    successMeasuresOther: '',
    averageEngagementLength: '',
    disengagementReason: '',
    disengagementReasonOther: '',
    investorPreference: '',
    investorPreferredTeamSize: '',
    investorSoloConcerns: [],
    investorSoloConcernsOther: '',
    investorMultiConcerns: [],
    investorMultiConcernsOther: '',
    investorBiasFrequency: '',
    navigatingBias: [],
    navigatingBiasOther: '',
    advisedAddCofounder: '',
    addCofounderFactors: [],
    addCofounderFactorsOther: '',
    resourceEffectiveness: 0,
    soloSupportGaps: [],
    soloSupportGapsOther: '',
    multiSupportGaps: [],
    multiSupportGapsOther: '',
    valuableResources: [],
    valuableResourcesOther: '',
    useAssessmentTool: '',
    toolFeatures: [],
    toolFeaturesOther: '',
    compensationModel: [],
    compensationModelOther: '',
    rateRange: '',
    reducedRates: '',
    biggestFrustration: '',
    biggestReward: '',
    biggestSuccessFactor: '',
    wishFoundersUnderstood: '',
    successStory: '',
    cautionaryTale: '',
    adviceToCoaches: '',
    anythingElse: '',
    openToFollowUp: ''
  });
  const toggleArrayItem = (array: string[], item: string) => {
    if (array.includes(item)) {
      return array.filter(i => i !== item);
    }
    return [...array, item];
  };
  const autoSaveProgress = async () => {
    setIsSaving(true);
    try {
      const metadata = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        referrer: document.referrer || 'Direct'
      };
      const submissionData = {
        ...formData,
        sessionId,
        completedSections: Array.from(completedSections).join(', '),
        currentSection,
        isComplete: false,
        metadata
      };
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });
      console.log('✅ Progress auto-saved');
    } catch (error) {
      console.error('❌ Auto-save error:', error);
    } finally {
      setIsSaving(false);
    }
  };
  const markSectionComplete = (sectionId: string) => {
    setCompletedSections(prev => new Set([...prev, sectionId]));
  };
  const goToNextSection = async () => {
    markSectionComplete(currentSection);
    // Auto-save progress
    await autoSaveProgress();
    const currentIndex = SECTIONS.findIndex(s => s.id === currentSection);
    if (currentIndex < SECTIONS.length - 1) {
      setCurrentSection(SECTIONS[currentIndex + 1].id);
      window.scrollTo(0, 0);
    }
  };
  // NEW: Handle section tab clicks with auto-save
  const handleSectionChange = async (sectionId: string) => {
    // Save current progress before switching
    await autoSaveProgress();
    setCurrentSection(sectionId);
    window.scrollTo(0, 0);
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // Collect metadata
      const metadata = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        referrer: document.referrer || 'Direct'
      };
      // Combine form data with metadata
      const submissionData = {
        ...formData,
        sessionId,
        completedSections: Array.from(completedSections).join(', '),
        currentSection: 'closing',
        isComplete: true,
        metadata
      };
      console.log('🚀 Submitting to:', GOOGLE_SCRIPT_URL);
      console.log('📝 Form data with metadata:', JSON.stringify(submissionData, null, 2));
      const response = await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });
      console.log('✅ Submission successful!');
      setIsSubmitted(true);
      // Redirect after 3 seconds
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);
    } catch (error) {
      console.error('❌ Submission error:', error);
      setSubmitError('There was an error submitting your response. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  if (isSubmitted) {
    return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-578">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-white rounded-[16px] p-12 max-w-md w-full text-center" data-id="element-579">
          <div className="w-16 h-16 bg-[#C5F467] rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-580">
            <Check className="w-8 h-8 text-[#1A3D3A]" data-id="element-581" />
          </div>
          <h2 className="text-[#1A3D3A] mb-4" style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: '32px',
          fontWeight: 500
        }} data-id="element-582">
            Thank You!
          </h2>
          <p className="text-[#3D5654] mb-8" style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '16px',
          lineHeight: 1.6
        }} data-id="element-583">
            Your insights are invaluable to understanding founder dynamics and
            improving support for entrepreneurs.
          </p>
          <p className="text-[#3D5654] text-sm" style={{
          fontFamily: "'DM Sans', sans-serif",
          opacity: 0.7
        }} data-id="element-584">
            Redirecting you to the home page...
          </p>
        </motion.div>
      </div>;
  }
  const renderSection = () => {
    switch (currentSection) {
      case 'welcome':
        return <div className="space-y-8" data-id="element-585">
            <div data-id="element-586">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-587">
                Email Address (optional)
              </label>
              <input type="email" value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="your@email.com" data-id="element-588" />
            </div>

            <div className="bg-[#F8FAF9] p-6 rounded-lg" data-id="element-589">
              <p className="text-[#3D5654]" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              lineHeight: 1.7
            }} data-id="element-590">
                Thank you for participating in this research on founder
                dynamics. This survey explores your experience working with both
                solo founders and multi-founder teams. Your insights will help
                us understand the unique challenges each founder structure faces
                and how coaches, mentors, and advisors can better support them.
                The survey takes approximately 10-15 minutes to complete.
              </p>
            </div>
          </div>;
      case 'background':
        return <div className="space-y-8" data-id="element-591">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-592">
              Your Background
            </h3>

            {/* Primary Role - SINGLE SELECT DROPDOWN */}
            <SingleSelectDropdown label="What is your primary role?" options={['Executive Coach', 'Startup Mentor', 'Advisor', 'Consultant', 'Therapist/Counselor', 'Investor (active coaching role)', 'Accelerator/Incubator Leader', 'Peer Group Facilitator', 'Other']} selected={formData.primaryRole} onChange={selected => setFormData({
            ...formData,
            primaryRole: selected
          })} placeholder="Select your role..." otherValue={formData.primaryRoleOther} onOtherChange={value => setFormData({
            ...formData,
            primaryRoleOther: value
          })} data-id="element-593" />

            {/* Years Working - SINGLE SELECT DROPDOWN */}
            <SingleSelectDropdown label="How long have you been working with founders?" options={['Less than 1 year', '1–3 years', '3–5 years', '5–10 years', 'More than 10 years']} selected={formData.yearsWorking} onChange={selected => setFormData({
            ...formData,
            yearsWorking: selected
          })} placeholder="Select duration..." data-id="element-594" />

            {/* Coaching Training */}
            <div data-id="element-595">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-596">
                Do you have formal coaching or mentoring training?
              </label>
              <div className="space-y-2" data-id="element-597">
                {['Yes, certified coach (ICF, etc.)', 'Yes, other formal training', 'No, but I have relevant experience', 'No'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-598">
                    <input type="radio" name="coachingTraining" value={option} checked={formData.coachingTraining === option} onChange={e => setFormData({
                  ...formData,
                  coachingTraining: e.target.value
                })} className="w-4 h-4" data-id="element-599" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-600">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Founder Experience */}
            <div data-id="element-601">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-602">
                Are you a founder yourself?
              </label>
              <div className="space-y-2" data-id="element-603">
                {['Yes, currently', 'Yes, previously', 'No'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-604">
                    <input type="radio" name="founderExperience" value={option} checked={formData.founderExperience === option} onChange={e => setFormData({
                  ...formData,
                  founderExperience: e.target.value
                })} className="w-4 h-4" data-id="element-605" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-606">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Solo or Multi Founder */}
            {(formData.founderExperience === 'Yes, currently' || formData.founderExperience === 'Yes, previously') && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} data-id="element-607">
                <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-608">
                  Were you a solo founder or part of a founding team?
                </label>
                <div className="space-y-2" data-id="element-609">
                  {['Solo founder', 'Part of a founding team', 'Both (different companies)'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-610">
                      <input type="radio" name="soloOrMulti" value={option} checked={formData.soloOrMulti === option} onChange={e => setFormData({
                  ...formData,
                  soloOrMulti: e.target.value
                })} className="w-4 h-4" data-id="element-611" />
                      <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-612">
                        {option}
                      </span>
                    </label>)}
                </div>
              </motion.div>}

            {/* Geographic Location */}
            <div data-id="element-613">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-614">
                Where are you primarily based?
              </label>
              <input type="text" value={formData.geographicLocation} onChange={e => setFormData({
              ...formData,
              geographicLocation: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="e.g., San Francisco, Remote, etc." data-id="element-615" />
            </div>

            {/* Work Remotely */}
            <div data-id="element-616">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-617">
                Do you work with founders remotely, in-person, or both?
              </label>
              <div className="space-y-2" data-id="element-618">
                {['Primarily remote', 'Primarily in-person', 'Both equally'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-619">
                    <input type="radio" name="workRemotely" value={option} checked={formData.workRemotely === option} onChange={e => setFormData({
                  ...formData,
                  workRemotely: e.target.value
                })} className="w-4 h-4" data-id="element-620" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-621">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>
          </div>;
      case 'clients':
        return <div className="space-y-8" data-id="element-622">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-623">
              Client Profile
            </h3>

            {/* Total Founding Teams - SINGLE SELECT DROPDOWN */}
            <SingleSelectDropdown label="Approximately how many founding teams have you worked with in total?" options={['1–10', '11–25', '26–50', '51–100', 'More than 100']} selected={formData.totalFoundingTeams} onChange={selected => setFormData({
            ...formData,
            totalFoundingTeams: selected
          })} placeholder="Select range..." data-id="element-624" />

            {/* Typical Founder Structure - SINGLE SELECT DROPDOWN */}
            <SingleSelectDropdown label="What is the typical founder structure of your clients?" options={['Mostly solo founders (70%+)', 'More solo than multi-founder', 'Roughly even mix', 'More multi-founder than solo', 'Mostly multi-founder teams (70%+)']} selected={formData.typicalFounderStructure} onChange={selected => setFormData({
            ...formData,
            typicalFounderStructure: selected
          })} placeholder="Select structure..." data-id="element-625" />

            {/* Common Team Size - SINGLE SELECT DROPDOWN */}
            <SingleSelectDropdown label="What is the most common team size among your multi-founder clients?" options={['2 cofounders', '3 cofounders', '4+ cofounders', 'Varies widely', 'Not applicable—I mostly work with solo founders']} selected={formData.commonTeamSize} onChange={selected => setFormData({
            ...formData,
            commonTeamSize: selected
          })} placeholder="Select team size..." data-id="element-626" />

            {/* Funding Stages - DROPDOWN */}
            <MultiSelectDropdown label="What funding stages do most of your clients fall into? (Select all that apply)" options={['Pre-Seed (idea/concept stage)', 'Seed (MVP/early traction)', 'Series A (scaling product and team)', 'Series B–C (growth stage)', 'Series D+/Exit stage', 'Bootstrapped/Non-venture track']} selected={formData.fundingStages} onChange={selected => setFormData({
            ...formData,
            fundingStages: selected
          })} placeholder="Select funding stages..." data-id="element-627" />

            {/* Industries - DROPDOWN */}
            <MultiSelectDropdown label="What industries do your clients typically operate in? (Select all that apply)" options={['SaaS/Software', 'Consumer Tech', 'Fintech', 'Healthcare/Biotech', 'E-commerce/Retail', 'Climate/Cleantech', 'AI/ML', 'EdTech', 'Hardware', 'Media/Entertainment', 'Professional Services', 'Marketplace', 'Other']} selected={formData.industries} onChange={selected => setFormData({
            ...formData,
            industries: selected
          })} placeholder="Select industries..." otherValue={formData.industriesOther} onOtherChange={value => setFormData({
            ...formData,
            industriesOther: value
          })} data-id="element-628" />

            {/* How Founders Find You - DROPDOWN */}
            <MultiSelectDropdown label="How do founders typically find you? (Select all that apply)" options={['Referrals from other founders', 'Referrals from investors', 'Accelerator/Incubator programs', 'LinkedIn or social media', 'Speaking engagements', 'Content/Thought leadership', 'Coaching marketplaces or platforms', 'Personal network', 'Other']} selected={formData.howFoundersFind} onChange={selected => setFormData({
            ...formData,
            howFoundersFind: selected
          })} placeholder="Select sources..." otherValue={formData.howFoundersFindOther} onOtherChange={value => setFormData({
            ...formData,
            howFoundersFindOther: value
          })} data-id="element-629" />

            {/* Founder Preference - SINGLE SELECT DROPDOWN */}
            <SingleSelectDropdown label="Do you have a preference for working with solo founders or multi-founder teams?" options={['Strongly prefer solo founders', 'Slightly prefer solo founders', 'No preference', 'Slightly prefer multi-founder teams', 'Strongly prefer multi-founder teams']} selected={formData.founderPreference} onChange={selected => setFormData({
            ...formData,
            founderPreference: selected
          })} placeholder="Select preference..." data-id="element-630" />
          </div>;
      case 'solo':
        return <div className="space-y-8" data-id="element-631">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-632">
              Solo Founder Challenges
            </h3>
            <p className="text-[#3D5654] text-sm -mt-4" style={{
            fontFamily: "'DM Sans', sans-serif"
          }} data-id="element-633">
              Skip this section if you don't work with solo founders
            </p>

            {/* Solo Founder Challenges - DROPDOWN */}
            <MultiSelectDropdown label="Which challenges do you most frequently observe in solo founders? (Select all that apply)" options={['Decision-making without a thought partner', 'Managing self-doubt or impostor syndrome', 'Emotional isolation and loneliness', 'Burnout and work-life imbalance', 'Skill gaps they struggle to fill', 'Difficulty delegating or letting go of control', 'Building trusted support networks', 'Financial or fundraising pressure', 'Accountability and follow-through', 'Investor bias against solo founders', 'Relationship strain (family/partner)', 'Identity fusion with the company', 'Difficulty asking for help', 'Analysis paralysis', 'Carrying full responsibility for failures', 'Slower execution due to bandwidth limitations', 'Sustaining motivation during slow phases', 'Other']} selected={formData.soloFounderChallenges} onChange={selected => setFormData({
            ...formData,
            soloFounderChallenges: selected
          })} placeholder="Select challenges..." otherValue={formData.soloFounderChallengesOther} onOtherChange={value => setFormData({
            ...formData,
            soloFounderChallengesOther: value
          })} data-id="element-634" />

            {/* Top Solo Challenge - SINGLE SELECT DROPDOWN */}
            <SingleSelectDropdown label="What is the #1 challenge solo founders struggle with most?" options={['Decision-making alone', 'Emotional isolation and loneliness', 'Burnout and exhaustion', 'Skill gaps', 'Investor perception and bias', 'Accountability', 'Bandwidth and execution limitations', 'Financial pressure', 'Other']} selected={formData.topSoloChallenge} onChange={selected => setFormData({
            ...formData,
            topSoloChallenge: selected
          })} placeholder="Select top challenge..." otherValue={formData.topSoloChallengeOther} onOtherChange={value => setFormData({
            ...formData,
            topSoloChallengeOther: value
          })} data-id="element-635" />

            {/* Solo Founder Triggers - DROPDOWN */}
            <MultiSelectDropdown label="What typically triggers a solo founder to seek coaching or mentorship? (Select all that apply)" options={['Burnout or exhaustion', 'Major decision or pivot', 'Fundraising process', 'Hiring challenges', 'Personal life strain', 'Loss of motivation', 'Feeling stuck or uncertain', 'Impostor syndrome', 'Desire for accountability', 'Preparing for exit', 'Loneliness or isolation', 'Scaling challenges', 'Other']} selected={formData.soloFounderTriggers} onChange={selected => setFormData({
            ...formData,
            soloFounderTriggers: selected
          })} placeholder="Select triggers..." otherValue={formData.soloFounderTriggersOther} onOtherChange={value => setFormData({
            ...formData,
            soloFounderTriggersOther: value
          })} data-id="element-636" />

            {/* Solo Founder Stage - SINGLE SELECT DROPDOWN */}
            <SingleSelectDropdown label="At what stage do solo founders typically seek out coaching or mentorship?" options={['Before launching', 'Early stage (0–1 year)', 'Growth stage (1–3 years)', 'Scaling stage (3+ years)', 'During crisis or major challenge', 'Varies widely']} selected={formData.soloFounderStage} onChange={selected => setFormData({
            ...formData,
            soloFounderStage: selected
          })} placeholder="Select stage..." data-id="element-637" />
          </div>;
      case 'multi':
        return <div className="space-y-8" data-id="element-638">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-639">
              Multi-Founder Challenges
            </h3>
            <p className="text-[#3D5654] text-sm -mt-4" style={{
            fontFamily: "'DM Sans', sans-serif"
          }} data-id="element-640">
              Skip this section if you don't work with multi-founder teams
            </p>

            {/* Multi-Founder Challenges - DROPDOWN */}
            <MultiSelectDropdown label="Which challenges do you most frequently observe in multi-founder teams? (Select all that apply)" options={['Cofounder conflict or disagreements', 'Misaligned vision or priorities', 'Unclear roles and responsibilities', 'Equity disputes or resentment', 'Communication breakdowns', 'Different work styles or commitment levels', 'Power struggles or ego clashes', 'Decision-making gridlock', 'One founder carrying more weight than others', 'Trust erosion over time', 'Difficulty removing an underperforming cofounder', 'Diverging life circumstances (family, finances, health)', 'Founding team breakups', 'Hiring decisions causing friction', 'Strategic disagreements', 'Unequal skill contribution', 'Passive-aggressive dynamics', 'Lack of honest feedback between cofounders', 'Other']} selected={formData.multiFounderChallenges} onChange={selected => setFormData({
            ...formData,
            multiFounderChallenges: selected
          })} placeholder="Select challenges..." otherValue={formData.multiFounderChallengesOther} onOtherChange={value => setFormData({
            ...formData,
            multiFounderChallengesOther: value
          })} data-id="element-641" />

            {/* Top Multi Challenge - SINGLE SELECT DROPDOWN */}
            <SingleSelectDropdown label="What is the #1 challenge multi-founder teams struggle with most?" options={['Cofounder conflict and disagreements', 'Misaligned vision or priorities', 'Unclear roles and responsibilities', 'Equity issues and resentment', 'Communication breakdown', 'Decision-making gridlock', 'Trust issues', 'Unequal commitment levels', 'Power dynamics', 'Other']} selected={formData.topMultiChallenge} onChange={selected => setFormData({
            ...formData,
            topMultiChallenge: selected
          })} placeholder="Select top challenge..." otherValue={formData.topMultiChallengeOther} onOtherChange={value => setFormData({
            ...formData,
            topMultiChallengeOther: value
          })} data-id="element-642" />

            {/* Cofounder Conflict Stage - SINGLE SELECT DROPDOWN */}
            <SingleSelectDropdown label="At what stage do cofounder conflicts typically emerge?" options={['Before launch', 'Early stage (0–1 year)', 'Growth stage (1–3 years)', 'Scaling stage (3+ years)', 'During fundraising', 'During crisis', 'Varies widely']} selected={formData.cofounderConflictStage} onChange={selected => setFormData({
            ...formData,
            cofounderConflictStage: selected
          })} placeholder="Select stage..." data-id="element-643" />

            {/* Cofounder Conflict Triggers - DROPDOWN */}
            <MultiSelectDropdown label="What typically triggers cofounder conflict? (Select all that apply)" options={['Disagreement on company direction', 'Unequal workload or contribution', 'Money or compensation disputes', 'Equity renegotiation', 'Hiring or firing decisions', 'Fundraising strategy', 'Personal life changes (marriage, kids, health)', 'Different risk tolerances', 'External pressure (investors, board)', 'Success and scaling challenges', 'Failure and company struggles', 'Personality clashes', 'Bringing on new team members', 'Exit or acquisition discussions', 'Other']} selected={formData.cofounderConflictTriggers} onChange={selected => setFormData({
            ...formData,
            cofounderConflictTriggers: selected
          })} placeholder="Select triggers..." otherValue={formData.cofounderConflictTriggersOther} onOtherChange={value => setFormData({
            ...formData,
            cofounderConflictTriggersOther: value
          })} data-id="element-644" />
          </div>;
      case 'comparing':
        return <div className="space-y-8" data-id="element-645">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-646">
              Comparing Solo vs Multi-Founder
            </h3>

            {/* Greater Challenges - SINGLE SELECT DROPDOWN */}
            <SingleSelectDropdown label="Overall, which founder structure faces greater challenges?" options={['Solo founders face significantly more challenges', 'Solo founders face somewhat more challenges', 'About equal but different types of challenges', 'Multi-founder teams face somewhat more challenges', 'Multi-founder teams face significantly more challenges', 'It depends entirely on the individuals involved']} selected={formData.greaterChallenges} onChange={selected => setFormData({
            ...formData,
            greaterChallenges: selected
          })} placeholder="Select answer..." data-id="element-647" />

            {/* Solo More Pronounced - DROPDOWN */}
            <MultiSelectDropdown label="Which challenges are MORE pronounced for solo founders compared to multi-founder teams? (Select all that apply)" options={['Emotional load and isolation', 'Decision-making confidence', 'Investor perception and bias', 'Personal accountability', 'Skill coverage gaps', 'Resilience during setbacks', 'Work-life balance', 'Loneliness', 'Bandwidth and execution speed', 'Building a support network', 'Burnout risk', 'None—challenges are about the same', 'Other']} selected={formData.soloMorePronounced} onChange={selected => setFormData({
            ...formData,
            soloMorePronounced: selected
          })} placeholder="Select challenges..." otherValue={formData.soloMorePronouncedOther} onOtherChange={value => setFormData({
            ...formData,
            soloMorePronouncedOther: value
          })} data-id="element-648" />

            {/* Multi More Pronounced - DROPDOWN */}
            <MultiSelectDropdown label="Which challenges are MORE pronounced for multi-founder teams compared to solo founders? (Select all that apply)" options={['Interpersonal conflict', 'Alignment on vision and strategy', 'Equity and compensation disputes', 'Communication overhead', 'Decision-making speed and gridlock', 'Power dynamics and ego', 'Trust maintenance', 'Role clarity and ownership', 'Cofounder breakups', 'Managing different commitment levels', 'Ego management', 'None—challenges are about the same', 'Other']} selected={formData.multiMorePronounced} onChange={selected => setFormData({
            ...formData,
            multiMorePronounced: selected
          })} placeholder="Select challenges..." otherValue={formData.multiMorePronouncedOther} onOtherChange={value => setFormData({
            ...formData,
            multiMorePronouncedOther: value
          })} data-id="element-649" />

            {/* Higher Success Rate - SINGLE SELECT DROPDOWN */}
            <SingleSelectDropdown label="Which founder structure do you believe has a higher success rate?" options={['Solo founders', 'Multi-founder teams (2 people)', 'Multi-founder teams (3+ people)', 'No meaningful difference', 'Success depends on other factors more than team structure']} selected={formData.higherSuccessRate} onChange={selected => setFormData({
            ...formData,
            higherSuccessRate: selected
          })} placeholder="Select answer..." data-id="element-650" />

            {/* Easier to Coach - SINGLE SELECT DROPDOWN */}
            <SingleSelectDropdown label="Which founder structure is easier to coach or advise?" options={['Solo founders—much easier', 'Solo founders—somewhat easier', 'About the same difficulty', 'Multi-founder teams—somewhat easier', 'Multi-founder teams—much easier', 'It depends on the specific situation']} selected={formData.easierToCoach} onChange={selected => setFormData({
            ...formData,
            easierToCoach: selected
          })} placeholder="Select answer..." data-id="element-651" />

            {/* Easier/Harder Explanation */}
            <div data-id="element-652">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-653">
                What makes one founder structure easier or harder to work with
                than the other?
              </label>
              <textarea value={formData.easierHarderExplanation} onChange={e => setFormData({
              ...formData,
              easierHarderExplanation: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your thoughts..." data-id="element-654" />
            </div>
          </div>;
      case 'relationships':
        return <div className="space-y-8" data-id="element-655">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-656">
              Cofounder Relationships
            </h3>

            {/* Relationship Work Frequency */}
            <div data-id="element-657">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-658">
                How often do you work on cofounder relationship issues with
                multi-founder teams?
              </label>
              <div className="space-y-2" data-id="element-659">
                {['Almost every engagement', 'Frequently', 'Sometimes', 'Rarely', 'Never'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-660">
                    <input type="radio" name="cofounderRelationshipFrequency" value={option} checked={formData.cofounderRelationshipFrequency === option} onChange={e => setFormData({
                  ...formData,
                  cofounderRelationshipFrequency: e.target.value
                })} className="w-4 h-4" data-id="element-661" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-662">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Cofounder Issues - DROPDOWN */}
            <MultiSelectDropdown label="What cofounder relationship issues do you address most often? (Select all that apply)" options={['Communication breakdowns', 'Role and responsibility clarity', 'Conflict resolution', 'Trust rebuilding', 'Equity or compensation discussions', 'Decision-making processes', 'Alignment on vision and strategy', 'Managing different working styles', 'Addressing underperformance', 'Mediating disagreements', 'Preparing for a cofounder exit', 'Succession planning', 'Other']} selected={formData.cofounderIssues} onChange={selected => setFormData({
            ...formData,
            cofounderIssues: selected
          })} placeholder="Select issues..." otherValue={formData.cofounderIssuesOther} onOtherChange={value => setFormData({
            ...formData,
            cofounderIssuesOther: value
          })} data-id="element-663" />

            {/* Relationship Breakdown Frequency */}
            <div data-id="element-664">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-665">
                How often do cofounder relationships break down entirely among
                your clients?
              </label>
              <div className="space-y-2" data-id="element-666">
                {['Very often (more than 50%)', 'Often (25–50%)', 'Sometimes (10–25%)', 'Rarely (less than 10%)', 'Never'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-667">
                    <input type="radio" name="relationshipBreakdownFrequency" value={option} checked={formData.relationshipBreakdownFrequency === option} onChange={e => setFormData({
                  ...formData,
                  relationshipBreakdownFrequency: e.target.value
                })} className="w-4 h-4" data-id="element-668" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-669">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Relationship Failure Outcome */}
            <div data-id="element-670">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-671">
                When cofounder relationships fail, what is the most common
                outcome?
              </label>
              <div className="space-y-2" data-id="element-672">
                {['One cofounder leaves amicably', 'One cofounder is pushed out', 'Company shuts down', 'Cofounders continue but dysfunctionally', 'Legal dispute', 'Buyout', 'Company pivots to solo founder structure', 'Other'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-673">
                    <input type="radio" name="relationshipFailureOutcome" value={option} checked={formData.relationshipFailureOutcome === option} onChange={e => setFormData({
                  ...formData,
                  relationshipFailureOutcome: e.target.value
                })} className="w-4 h-4" data-id="element-674" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-675">
                      {option}
                    </span>
                  </label>)}
              </div>
              {formData.relationshipFailureOutcome === 'Other' && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} className="mt-3" data-id="element-676">
                  <input type="text" value={formData.relationshipFailureOutcomeOther} onChange={e => setFormData({
                ...formData,
                relationshipFailureOutcomeOther: e.target.value
              })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
                fontFamily: "'DM Sans', sans-serif"
              }} placeholder="Please specify..." data-id="element-677" />
                </motion.div>}
            </div>

            {/* Effective Interventions - DROPDOWN */}
            <MultiSelectDropdown label="What interventions are most effective for cofounder conflict? (Select all that apply)" options={['Facilitated difficult conversations', 'Role and responsibility restructuring', 'Formal operating agreements', 'Regular cofounder check-ins', 'Third-party mediation', 'Individual coaching for each cofounder', 'Equity restructuring', 'Creating decision-making frameworks', 'Establishing communication norms', 'Sometimes separation is the healthiest answer', 'Other']} selected={formData.effectiveInterventions} onChange={selected => setFormData({
            ...formData,
            effectiveInterventions: selected
          })} placeholder="Select interventions..." otherValue={formData.effectiveInterventionsOther} onOtherChange={value => setFormData({
            ...formData,
            effectiveInterventionsOther: value
          })} data-id="element-678" />
          </div>;
      case 'selection':
        return <div className="space-y-8" data-id="element-679">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-680">
              Cofounder Selection
            </h3>

            {/* Cofounder Selection Importance - Likert Scale */}
            <div data-id="element-681">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-682">
                How important is cofounder selection to startup success?
              </label>
              <p className="text-[#3D5654] mb-4 text-sm" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-683">
                1 = Not important at all → 5 = Critically important
              </p>
              <div className="flex gap-4 justify-center" data-id="element-684">
                {[1, 2, 3, 4, 5].map(num => <button key={num} onClick={() => setFormData({
                ...formData,
                cofounderSelectionImportance: num
              })} className={`w-14 h-14 rounded-full border-2 transition-all flex items-center justify-center ${formData.cofounderSelectionImportance === num ? 'bg-[#1A3D3A] text-white border-[#1A3D3A] scale-110' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40 hover:scale-105'}`} style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '20px',
                fontWeight: 600
              }} data-id="element-685">
                    {num}
                  </button>)}
              </div>
            </div>

            {/* Cofounder Mistakes - DROPDOWN */}
            <MultiSelectDropdown label="What are the biggest mistakes founders make when choosing a cofounder? (Select all that apply)" options={['Choosing a friend without evaluating fit', 'Not discussing equity upfront', 'Assuming alignment without explicit conversations', 'Prioritizing skills over values alignment', 'Moving too fast', 'Not doing a trial period first', 'Ignoring red flags', 'Choosing someone too similar to themselves', 'Choosing someone too different', 'Not formalizing agreements legally', 'Overvaluing technical skills', 'Undervaluing emotional intelligence', 'Not stress-testing the relationship', 'Other']} selected={formData.cofounderMistakes} onChange={selected => setFormData({
            ...formData,
            cofounderMistakes: selected
          })} placeholder="Select mistakes..." otherValue={formData.cofounderMistakesOther} onOtherChange={value => setFormData({
            ...formData,
            cofounderMistakesOther: value
          })} data-id="element-686" />

            {/* Cofounder Priorities */}
            <div data-id="element-687">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-688">
                What should founders prioritize most when selecting a cofounder?
                (Select top 3)
              </label>
              <div className="space-y-2" data-id="element-689">
                {['Complementary skills', 'Shared values', 'Trust and communication', 'Work style compatibility', 'Similar commitment level', 'Aligned vision', 'Financial alignment', 'Prior working relationship', 'Domain expertise', 'Emotional resilience', 'Network and connections'].map(priority => <label key={priority} className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition-colors ${formData.cofounderPriorities.includes(priority) ? 'border-[#1A3D3A] bg-[#1A3D3A]/5' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40'} ${formData.cofounderPriorities.length >= 3 && !formData.cofounderPriorities.includes(priority) ? 'opacity-50' : ''}`} data-id="element-690">
                    <input type="checkbox" checked={formData.cofounderPriorities.includes(priority)} onChange={() => setFormData({
                  ...formData,
                  cofounderPriorities: toggleArrayItem(formData.cofounderPriorities, priority)
                })} disabled={formData.cofounderPriorities.length >= 3 && !formData.cofounderPriorities.includes(priority)} className="w-4 h-4" data-id="element-691" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-692">
                      {priority}
                    </span>
                  </label>)}
              </div>
              <p className="text-[#3D5654] text-sm mt-2" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-693">
                {formData.cofounderPriorities.length}/3 selected
              </p>
            </div>

            {/* Cofounder Red Flags - DROPDOWN */}
            <MultiSelectDropdown label="What red flags do you look for in potential cofounder relationships? (Select all that apply)" options={['Misaligned motivations (money vs mission)', 'Different time horizons', 'Unequal commitment levels', 'Poor communication patterns', 'History of conflict', 'Avoiding difficult conversations', 'Unclear role division', 'One person dominating', 'Significantly different risk tolerance', 'Values misalignment', 'Different definitions of success', 'Financial desperation in one party', 'Reluctance to formalize agreements', 'Other']} selected={formData.cofounderRedFlags} onChange={selected => setFormData({
            ...formData,
            cofounderRedFlags: selected
          })} placeholder="Select red flags..." otherValue={formData.cofounderRedFlagsOther} onOtherChange={value => setFormData({
            ...formData,
            cofounderRedFlagsOther: value
          })} data-id="element-694" />
          </div>;
      case 'composition':
        return <div className="space-y-8" data-id="element-695">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-696">
              Team Composition
            </h3>

            {/* Ideal Team Size */}
            <div data-id="element-697">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-698">
                What do you believe is the ideal founding team size?
              </label>
              <div className="space-y-2" data-id="element-699">
                {['1 (solo founder)', '2 cofounders', '3 cofounders', '4+ cofounders', 'Depends on the business type', 'No ideal—it depends on the individuals'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-700">
                    <input type="radio" name="idealTeamSize" value={option} checked={formData.idealTeamSize === option} onChange={e => setFormData({
                  ...formData,
                  idealTeamSize: e.target.value
                })} className="w-4 h-4" data-id="element-701" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-702">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Best Combinations - DROPDOWN */}
            <MultiSelectDropdown label="What cofounder combinations work best? (Select all that apply)" options={['Technical + Business', 'Technical + Technical', 'Business + Business', 'Product + Engineering', 'Visionary + Operator', 'Industry Expert + Generalist', 'Hustler + Builder', 'Previous colleagues', 'Friends who have stress-tested the relationship', 'Strangers with complementary skills', 'No particular pattern predicts success', 'Other']} selected={formData.bestCombinations} onChange={selected => setFormData({
            ...formData,
            bestCombinations: selected
          })} placeholder="Select combinations..." otherValue={formData.bestCombinationsOther} onOtherChange={value => setFormData({
            ...formData,
            bestCombinationsOther: value
          })} data-id="element-703" />

            {/* Struggling Combinations - DROPDOWN */}
            <MultiSelectDropdown label="What cofounder combinations tend to struggle most? (Select all that apply)" options={['Two visionaries with no executor', 'All technical with no business skills', 'All business with no technical skills', 'Friends who have not worked together before', 'Romantic partners', 'Family members', 'Large equity gap between cofounders', 'Large experience gap', 'Very similar personalities', 'Founders who met very recently', 'Remote-only cofounders who have never met in person', 'Other']} selected={formData.strugglingCombinations} onChange={selected => setFormData({
            ...formData,
            strugglingCombinations: selected
          })} placeholder="Select combinations..." otherValue={formData.strugglingCombinationsOther} onOtherChange={value => setFormData({
            ...formData,
            strugglingCombinationsOther: value
          })} data-id="element-704" />

            {/* Worked Together Importance */}
            <div data-id="element-705">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-706">
                How important is it for cofounders to have worked together
                before?
              </label>
              <p className="text-[#3D5654] mb-4 text-sm" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-707">
                1 = Not important at all → 5 = Absolutely essential
              </p>
              <div className="flex gap-4 justify-center" data-id="element-708">
                {[1, 2, 3, 4, 5].map(num => <button key={num} onClick={() => setFormData({
                ...formData,
                workedTogetherImportance: num
              })} className={`w-14 h-14 rounded-full border-2 transition-all flex items-center justify-center ${formData.workedTogetherImportance === num ? 'bg-[#1A3D3A] text-white border-[#1A3D3A] scale-110' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40 hover:scale-105'}`} style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '20px',
                fontWeight: 600
              }} data-id="element-709">
                    {num}
                  </button>)}
              </div>
            </div>

            {/* Complementary Skills Importance */}
            <div data-id="element-710">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-711">
                How important is it for cofounders to have complementary skills?
              </label>
              <p className="text-[#3D5654] mb-4 text-sm" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-712">
                1 = Not important at all → 5 = Absolutely essential
              </p>
              <div className="flex gap-4 justify-center" data-id="element-713">
                {[1, 2, 3, 4, 5].map(num => <button key={num} onClick={() => setFormData({
                ...formData,
                complementarySkillsImportance: num
              })} className={`w-14 h-14 rounded-full border-2 transition-all flex items-center justify-center ${formData.complementarySkillsImportance === num ? 'bg-[#1A3D3A] text-white border-[#1A3D3A] scale-110' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40 hover:scale-105'}`} style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '20px',
                fontWeight: 600
              }} data-id="element-714">
                    {num}
                  </button>)}
              </div>
            </div>

            {/* Aligned Values Importance */}
            <div data-id="element-715">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-716">
                How important is it for cofounders to have aligned values?
              </label>
              <p className="text-[#3D5654] mb-4 text-sm" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-717">
                1 = Not important at all → 5 = Absolutely essential
              </p>
              <div className="flex gap-4 justify-center" data-id="element-718">
                {[1, 2, 3, 4, 5].map(num => <button key={num} onClick={() => setFormData({
                ...formData,
                alignedValuesImportance: num
              })} className={`w-14 h-14 rounded-full border-2 transition-all flex items-center justify-center ${formData.alignedValuesImportance === num ? 'bg-[#1A3D3A] text-white border-[#1A3D3A] scale-110' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40 hover:scale-105'}`} style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '20px',
                fontWeight: 600
              }} data-id="element-719">
                    {num}
                  </button>)}
              </div>
            </div>
          </div>;
      case 'decisions':
        return <div className="space-y-8" data-id="element-720">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-721">
              Decision-Making & Governance
            </h3>

            {/* Best Decision Structures - DROPDOWN */}
            <MultiSelectDropdown label="What decision-making structures work best for multi-founder teams? (Select all that apply)" options={['Consensus on all major decisions', 'CEO has final say on everything', 'Domain-based ownership (each founder owns decisions in their area)', 'Majority vote', 'Rotating decision authority', 'Formal decision-making framework', 'Informal case-by-case approach', 'Designated tiebreaker (advisor or board member)', 'Other']} selected={formData.bestDecisionStructures} onChange={selected => setFormData({
            ...formData,
            bestDecisionStructures: selected
          })} placeholder="Select structures..." otherValue={formData.bestDecisionStructuresOther} onOtherChange={value => setFormData({
            ...formData,
            bestDecisionStructuresOther: value
          })} data-id="element-722" />

            {/* Decision Breakdown Causes - DROPDOWN */}
            <MultiSelectDropdown label="What causes decision-making breakdowns in multi-founder teams? (Select all that apply)" options={['No clear decision-making process', 'Inability to disagree openly', 'Avoidance of conflict', 'One founder dominating', 'Lack of trust', 'Different risk tolerances', 'Analysis paralysis', 'Undermining decisions after they are made', 'Revisiting closed decisions repeatedly', 'External pressure from investors', 'Lack of role clarity', 'Different information access', 'Other']} selected={formData.decisionBreakdownCauses} onChange={selected => setFormData({
            ...formData,
            decisionBreakdownCauses: selected
          })} placeholder="Select causes..." otherValue={formData.decisionBreakdownCausesOther} onOtherChange={value => setFormData({
            ...formData,
            decisionBreakdownCausesOther: value
          })} data-id="element-723" />

            {/* Solo vs Multi Decisions */}
            <div data-id="element-724">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-725">
                How do solo founders make better or worse decisions compared to
                multi-founder teams?
              </label>
              <textarea value={formData.soloVsMultiDecisions} onChange={e => setFormData({
              ...formData,
              soloVsMultiDecisions: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your observations..." data-id="element-726" />
            </div>
          </div>;
      case 'equity':
        return <div className="space-y-8" data-id="element-727">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-728">
              Equity & Compensation
            </h3>

            {/* Equity Issues Frequency */}
            <div data-id="element-729">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-730">
                How often do equity issues cause problems in multi-founder
                teams?
              </label>
              <div className="space-y-2" data-id="element-731">
                {['Almost always', 'Frequently', 'Sometimes', 'Rarely', 'Never'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-732">
                    <input type="radio" name="equityIssuesFrequency" value={option} checked={formData.equityIssuesFrequency === option} onChange={e => setFormData({
                  ...formData,
                  equityIssuesFrequency: e.target.value
                })} className="w-4 h-4" data-id="element-733" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-734">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Common Equity Splits */}
            <div data-id="element-735">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-736">
                What equity splits do you most commonly see among cofounders?
              </label>
              <div className="space-y-2" data-id="element-737">
                {['Equal split (50/50 or 33/33/33 etc)', 'Slightly unequal (60/40 etc)', 'Significantly unequal (70/30 or more)', 'Varies widely'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-738">
                    <input type="radio" name="commonEquitySplits" value={option} checked={formData.commonEquitySplits === option} onChange={e => setFormData({
                  ...formData,
                  commonEquitySplits: e.target.value
                })} className="w-4 h-4" data-id="element-739" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-740">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Recommended Equity Split */}
            <div data-id="element-741">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-742">
                What equity split do you generally recommend?
              </label>
              <div className="space-y-2" data-id="element-743">
                {['Always equal', 'Usually equal with minor adjustments', 'Based on contribution and role', 'Based on idea origination', 'Depends entirely on the situation', 'I do not advise on equity matters'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-744">
                    <input type="radio" name="recommendedEquitySplit" value={option} checked={formData.recommendedEquitySplit === option} onChange={e => setFormData({
                  ...formData,
                  recommendedEquitySplit: e.target.value
                })} className="w-4 h-4 flex-shrink-0" data-id="element-745" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-746">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Equity Conflict Issues - DROPDOWN */}
            <MultiSelectDropdown label="What equity-related issues cause the most conflict? (Select all that apply)" options={['Initial split disagreement', 'Resentment over unfair split', 'Vesting terms and cliff', 'One founder leaving before fully vesting', 'Dilution from fundraising', 'Adding new cofounders later', 'Compensation vs equity tradeoffs', 'Founder not contributing equally to their equity share', 'Lack of legal documentation', 'Renegotiation requests', 'Other']} selected={formData.equityConflictIssues} onChange={selected => setFormData({
            ...formData,
            equityConflictIssues: selected
          })} placeholder="Select issues..." otherValue={formData.equityConflictIssuesOther} onOtherChange={value => setFormData({
            ...formData,
            equityConflictIssuesOther: value
          })} data-id="element-747" />

            {/* Recommend Cofounder Agreements */}
            <div data-id="element-748">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-749">
                Do you recommend cofounder agreements or operating agreements?
              </label>
              <div className="space-y-2" data-id="element-750">
                {['Always—it is essential', 'Usually—strongly recommended', 'Sometimes—depends on the situation', 'Rarely', 'Never'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-751">
                    <input type="radio" name="recommendCofounderAgreements" value={option} checked={formData.recommendCofounderAgreements === option} onChange={e => setFormData({
                  ...formData,
                  recommendCofounderAgreements: e.target.value
                })} className="w-4 h-4" data-id="element-752" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-753">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>
          </div>;
      case 'transitions':
        return <div className="space-y-8" data-id="element-754">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-755">
              Founder Transitions
            </h3>

            {/* Solo Add Cofounder Frequency */}
            <div data-id="element-756">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-757">
                How often do you see solo founders add a cofounder later?
              </label>
              <div className="space-y-2" data-id="element-758">
                {['Frequently', 'Sometimes', 'Rarely', 'Never'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-759">
                      <input type="radio" name="soloAddCofounderFrequency" value={option} checked={formData.soloAddCofounderFrequency === option} onChange={e => setFormData({
                  ...formData,
                  soloAddCofounderFrequency: e.target.value
                })} className="w-4 h-4" data-id="element-760" />
                      <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-761">
                        {option}
                      </span>
                    </label>)}
              </div>
            </div>

            {/* Solo Seek Cofounder Reasons - DROPDOWN */}
            <MultiSelectDropdown label="What prompts solo founders to seek a cofounder? (Select all that apply)" options={['Investor pressure', 'Critical skill gaps', 'Burnout or overwhelm', 'Loneliness and isolation', 'Specific business need', 'Found the right person organically', 'Strategic partnership opportunity', 'Scaling challenges', 'Desire for a thought partner', 'Other']} selected={formData.soloSeekCofounderReasons} onChange={selected => setFormData({
            ...formData,
            soloSeekCofounderReasons: selected
          })} placeholder="Select reasons..." otherValue={formData.soloSeekCofounderReasonsOther} onOtherChange={value => setFormData({
            ...formData,
            soloSeekCofounderReasonsOther: value
          })} data-id="element-762" />

            {/* Late Cofounder Success Rate */}
            <div data-id="element-763">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-764">
                How often do late-stage cofounder additions succeed?
              </label>
              <div className="space-y-2" data-id="element-765">
                {['Usually succeed', 'Sometimes succeed', 'Rarely succeed', 'Almost never succeed', 'Too varied to generalize'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-766">
                    <input type="radio" name="lateCofounderSuccessRate" value={option} checked={formData.lateCofounderSuccessRate === option} onChange={e => setFormData({
                  ...formData,
                  lateCofounderSuccessRate: e.target.value
                })} className="w-4 h-4" data-id="element-767" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-768">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Late Cofounder Success Factors */}
            <div data-id="element-769">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-770">
                What makes late-stage cofounder additions succeed or fail?
              </label>
              <textarea value={formData.lateCofounderSuccessFactors} onChange={e => setFormData({
              ...formData,
              lateCofounderSuccessFactors: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your insights..." data-id="element-771" />
            </div>

            {/* Cofounder Departure Frequency */}
            <div data-id="element-772">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-773">
                How often do multi-founder teams lose a cofounder and continue
                operating?
              </label>
              <div className="space-y-2" data-id="element-774">
                {['Frequently', 'Sometimes', 'Rarely', 'Never'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-775">
                      <input type="radio" name="cofounderDepartureFrequency" value={option} checked={formData.cofounderDepartureFrequency === option} onChange={e => setFormData({
                  ...formData,
                  cofounderDepartureFrequency: e.target.value
                })} className="w-4 h-4" data-id="element-776" />
                      <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-777">
                        {option}
                      </span>
                    </label>)}
              </div>
            </div>

            {/* Survival Factors - DROPDOWN */}
            <MultiSelectDropdown label="What determines whether a company survives a cofounder departure? (Select all that apply)" options={['Which cofounder leaves', 'Stage of the company', 'Reason for departure', 'How the departure is handled', 'Remaining team strength', 'Investor support', 'Customer relationships', 'Legal and equity resolution', 'Company culture', 'Other']} selected={formData.survivalFactors} onChange={selected => setFormData({
            ...formData,
            survivalFactors: selected
          })} placeholder="Select factors..." otherValue={formData.survivalFactorsOther} onOtherChange={value => setFormData({
            ...formData,
            survivalFactorsOther: value
          })} data-id="element-778" />
          </div>;
      case 'approach':
        return <div className="space-y-8" data-id="element-779">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-780">
              Your Coaching Approach
            </h3>

            {/* Different Approach */}
            <div data-id="element-781">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-782">
                Do you approach coaching differently for solo founders vs
                multi-founder teams?
              </label>
              <div className="space-y-2" data-id="element-783">
                {['Yes, significantly different approaches', 'Yes, somewhat different approaches', 'No, my approach is essentially the same', 'It depends on the specific situation'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-784">
                    <input type="radio" name="differentApproach" value={option} checked={formData.differentApproach === option} onChange={e => setFormData({
                  ...formData,
                  differentApproach: e.target.value
                })} className="w-4 h-4 flex-shrink-0" data-id="element-785" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-786">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Solo Founder Differences - DROPDOWN */}
            <MultiSelectDropdown label="If yes—what do you do differently for solo founders? (Select all that apply)" options={['More focus on emotional support', 'More focus on accountability structures', 'More focus on building external support network', 'More focus on decision-making frameworks', 'More frequent check-ins', 'Help them find a pseudo-cofounder or thought partner', 'Address isolation more directly', 'Different goal-setting approach', 'More emphasis on self-care and boundaries', 'Not applicable—I use the same approach', 'Other']} selected={formData.soloFounderDifferences} onChange={selected => setFormData({
            ...formData,
            soloFounderDifferences: selected
          })} placeholder="Select differences..." otherValue={formData.soloFounderDifferencesOther} onOtherChange={value => setFormData({
            ...formData,
            soloFounderDifferencesOther: value
          })} data-id="element-787" />

            {/* Who You Work With */}
            <div data-id="element-788">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-789">
                When working with multi-founder teams, who do you typically work
                with?
              </label>
              <div className="space-y-2" data-id="element-790">
                {['All cofounders together', 'Each cofounder individually', 'CEO or lead founder only', 'Combination of group and individual sessions', 'Varies by engagement'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-791">
                    <input type="radio" name="whoYouWorkWith" value={option} checked={formData.whoYouWorkWith === option} onChange={e => setFormData({
                  ...formData,
                  whoYouWorkWith: e.target.value
                })} className="w-4 h-4 flex-shrink-0" data-id="element-792" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-793">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Conflicting Perspectives - DROPDOWN */}
            <MultiSelectDropdown label="What is your approach when cofounders have conflicting perspectives? (Select all that apply)" options={['Facilitate joint conversations', 'Meet individually first then bring together', 'Help them develop shared frameworks', 'Remain neutral throughout', 'Advocate for the company interest', 'Help them surface underlying issues', 'Refer to a specialist (mediator or therapist)', 'Sometimes recommend separation', 'Other']} selected={formData.conflictingPerspectives} onChange={selected => setFormData({
            ...formData,
            conflictingPerspectives: selected
          })} placeholder="Select approaches..." otherValue={formData.conflictingPerspectivesOther} onOtherChange={value => setFormData({
            ...formData,
            conflictingPerspectivesOther: value
          })} data-id="element-794" />

            {/* Coaching Style */}
            <div data-id="element-795">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-796">
                What is your primary coaching or mentoring style?
              </label>
              <div className="space-y-2" data-id="element-797">
                {['Directive—I provide specific guidance and advice', 'Non-directive—I ask questions and facilitate their thinking', 'Situational—I adapt based on what is needed', 'Accountability-focused', 'Therapeutic and emotional support', 'Peer-based—sharing my own experience', 'Other'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-798">
                    <input type="radio" name="coachingStyle" value={option} checked={formData.coachingStyle === option} onChange={e => setFormData({
                  ...formData,
                  coachingStyle: e.target.value
                })} className="w-4 h-4 flex-shrink-0" data-id="element-799" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-800">
                      {option}
                    </span>
                  </label>)}
              </div>
              {formData.coachingStyle === 'Other' && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} className="mt-3" data-id="element-801">
                  <input type="text" value={formData.coachingStyleOther} onChange={e => setFormData({
                ...formData,
                coachingStyleOther: e.target.value
              })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
                fontFamily: "'DM Sans', sans-serif"
              }} placeholder="Please specify..." data-id="element-802" />
                </motion.div>}
            </div>

            {/* Engagement Structure */}
            <div data-id="element-803">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-804">
                How do you typically structure engagements?
              </label>
              <div className="space-y-2" data-id="element-805">
                {['Ongoing retainer', 'Fixed-term engagement', 'Ad-hoc as needed', 'Cohort-based program', 'One-time intensive', 'Varies by client'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-806">
                    <input type="radio" name="engagementStructure" value={option} checked={formData.engagementStructure === option} onChange={e => setFormData({
                  ...formData,
                  engagementStructure: e.target.value
                })} className="w-4 h-4" data-id="element-807" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-808">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Meeting Frequency */}
            <div data-id="element-809">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-810">
                How often do you typically meet with clients?
              </label>
              <div className="space-y-2" data-id="element-811">
                {['Weekly', 'Bi-weekly', 'Monthly', 'Quarterly', 'As needed', 'Varies by client'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-812">
                    <input type="radio" name="meetingFrequency" value={option} checked={formData.meetingFrequency === option} onChange={e => setFormData({
                  ...formData,
                  meetingFrequency: e.target.value
                })} className="w-4 h-4" data-id="element-813" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-814">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Formats - DROPDOWN */}
            <MultiSelectDropdown label="What formats do you use? (Select all that apply)" options={['1:1 video calls', '1:1 in-person meetings', 'Group coaching', 'Async messaging (Slack, text, etc.)', 'Email', 'Workshops', 'Retreats', 'Other']} selected={formData.formats} onChange={selected => setFormData({
            ...formData,
            formats: selected
          })} placeholder="Select formats..." otherValue={formData.formatsOther} onOtherChange={value => setFormData({
            ...formData,
            formatsOther: value
          })} data-id="element-815" />

            {/* Tools Frameworks - DROPDOWN */}
            <MultiSelectDropdown label="What tools or frameworks do you commonly use? (Select all that apply)" options={['OKRs or goal-setting frameworks', 'Decision-making frameworks', 'Personality assessments (DISC, Myers-Briggs, etc.)', 'Strength assessments', 'Journaling or reflection exercises', 'Mindfulness or stress-reduction techniques', 'Accountability systems', 'Time management tools', 'Energy management frameworks', 'Cofounder relationship assessments', 'Communication frameworks', 'Conflict resolution processes', 'None specifically', 'Other']} selected={formData.toolsFrameworks} onChange={selected => setFormData({
            ...formData,
            toolsFrameworks: selected
          })} placeholder="Select tools..." otherValue={formData.toolsFrameworksOther} onOtherChange={value => setFormData({
            ...formData,
            toolsFrameworksOther: value
          })} data-id="element-816" />
          </div>;
      case 'outcomes':
        return <div className="space-y-8" data-id="element-817">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-818">
              Effectiveness & Outcomes
            </h3>

            {/* Founder Preparedness - Likert */}
            <div data-id="element-819">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-820">
                On a scale of 1–5, how prepared do most founders seem when they
                first engage with you?
              </label>
              <p className="text-[#3D5654] mb-4 text-sm" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-821">
                1 = Very unprepared → 5 = Very well prepared
              </p>
              <div className="flex gap-4 justify-center" data-id="element-822">
                {[1, 2, 3, 4, 5].map(num => <button key={num} onClick={() => setFormData({
                ...formData,
                founderPreparedness: num
              })} className={`w-14 h-14 rounded-full border-2 transition-all flex items-center justify-center ${formData.founderPreparedness === num ? 'bg-[#1A3D3A] text-white border-[#1A3D3A] scale-110' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40 hover:scale-105'}`} style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '20px',
                fontWeight: 600
              }} data-id="element-823">
                    {num}
                  </button>)}
              </div>
            </div>

            {/* Success Measures - DROPDOWN */}
            <MultiSelectDropdown label="How do you measure success in your engagements? (Select all that apply)" options={['Founder self-reported satisfaction', 'Business metrics (revenue, growth, etc.)', 'Fundraising success', 'Reduced stress or burnout', 'Improved decision-making confidence', 'Better work-life balance', 'Achievement of stated goals', 'Founder retention (continued engagement)', 'Improved cofounder relationship', 'Qualitative feedback', 'I do not formally measure success', 'Other']} selected={formData.successMeasures} onChange={selected => setFormData({
            ...formData,
            successMeasures: selected
          })} placeholder="Select measures..." otherValue={formData.successMeasuresOther} onOtherChange={value => setFormData({
            ...formData,
            successMeasuresOther: value
          })} data-id="element-824" />

            {/* Average Engagement Length */}
            <div data-id="element-825">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-826">
                What is the average length of your engagements?
              </label>
              <div className="space-y-2" data-id="element-827">
                {['Less than 3 months', '3–6 months', '6–12 months', '1–2 years', 'More than 2 years', 'Varies widely'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-828">
                    <input type="radio" name="averageEngagementLength" value={option} checked={formData.averageEngagementLength === option} onChange={e => setFormData({
                  ...formData,
                  averageEngagementLength: e.target.value
                })} className="w-4 h-4" data-id="element-829" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-830">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Disengagement Reason */}
            <div data-id="element-831">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-832">
                What is the most common reason founders disengage from coaching?
              </label>
              <div className="space-y-2" data-id="element-833">
                {['Achieved their goals', 'Ran out of money', 'Did not see value', 'Got too busy', 'Company shut down', 'Found a cofounder or partner', 'Outgrew the engagement', 'No specific reason given', 'Other'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-834">
                    <input type="radio" name="disengagementReason" value={option} checked={formData.disengagementReason === option} onChange={e => setFormData({
                  ...formData,
                  disengagementReason: e.target.value
                })} className="w-4 h-4" data-id="element-835" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-836">
                      {option}
                    </span>
                  </label>)}
              </div>
              {formData.disengagementReason === 'Other' && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} className="mt-3" data-id="element-837">
                  <input type="text" value={formData.disengagementReasonOther} onChange={e => setFormData({
                ...formData,
                disengagementReasonOther: e.target.value
              })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
                fontFamily: "'DM Sans', sans-serif"
              }} placeholder="Please specify..." data-id="element-838" />
                </motion.div>}
            </div>
          </div>;
      case 'investors':
        return <div className="space-y-8" data-id="element-839">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-840">
              Investor Perspectives
            </h3>

            {/* Investor Preference */}
            <div data-id="element-841">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-842">
                Do investors in your network prefer solo founders or
                multi-founder teams?
              </label>
              <div className="space-y-2" data-id="element-843">
                {['Strongly prefer solo founders', 'Slightly prefer solo founders', 'No strong preference', 'Slightly prefer multi-founder teams', 'Strongly prefer multi-founder teams', 'Varies significantly by investor'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-844">
                    <input type="radio" name="investorPreference" value={option} checked={formData.investorPreference === option} onChange={e => setFormData({
                  ...formData,
                  investorPreference: e.target.value
                })} className="w-4 h-4 flex-shrink-0" data-id="element-845" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-846">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Investor Preferred Team Size */}
            <div data-id="element-847">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-848">
                What team size do investors most prefer?
              </label>
              <div className="space-y-2" data-id="element-849">
                {['Solo founder', '2 cofounders', '3 cofounders', '4+ cofounders', 'No preference', 'Depends on other factors'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-850">
                    <input type="radio" name="investorPreferredTeamSize" value={option} checked={formData.investorPreferredTeamSize === option} onChange={e => setFormData({
                  ...formData,
                  investorPreferredTeamSize: e.target.value
                })} className="w-4 h-4" data-id="element-851" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-852">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Investor Solo Concerns - DROPDOWN */}
            <MultiSelectDropdown label="What concerns do investors raise about solo founders? (Select all that apply)" options={['Key-person risk', 'Ability to scale', 'Bandwidth limitations', 'Lack of accountability', 'Skill gaps', 'Resilience concerns', 'Fundraising ability', 'None—investors do not raise concerns', 'Other']} selected={formData.investorSoloConcerns} onChange={selected => setFormData({
            ...formData,
            investorSoloConcerns: selected
          })} placeholder="Select concerns..." otherValue={formData.investorSoloConcernsOther} onOtherChange={value => setFormData({
            ...formData,
            investorSoloConcernsOther: value
          })} data-id="element-853" />

            {/* Investor Multi Concerns - DROPDOWN */}
            <MultiSelectDropdown label="What concerns do investors raise about multi-founder teams? (Select all that apply)" options={['Cofounder conflict risk', 'Equity structure issues', 'Decision-making speed', 'Alignment concerns', 'Communication issues', 'One founder carrying the team', 'Unclear leadership', 'Too many founders', 'None—investors do not raise concerns', 'Other']} selected={formData.investorMultiConcerns} onChange={selected => setFormData({
            ...formData,
            investorMultiConcerns: selected
          })} placeholder="Select concerns..." otherValue={formData.investorMultiConcernsOther} onOtherChange={value => setFormData({
            ...formData,
            investorMultiConcernsOther: value
          })} data-id="element-854" />

            {/* Investor Bias Frequency */}
            <div data-id="element-855">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-856">
                How often do your clients face explicit bias from investors
                based on team structure?
              </label>
              <div className="space-y-2" data-id="element-857">
                {['Almost always', 'Often', 'Sometimes', 'Rarely', 'Never', 'Not applicable—my clients do not fundraise'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-858">
                    <input type="radio" name="investorBiasFrequency" value={option} checked={formData.investorBiasFrequency === option} onChange={e => setFormData({
                  ...formData,
                  investorBiasFrequency: e.target.value
                })} className="w-4 h-4 flex-shrink-0" data-id="element-859" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-860">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Navigating Bias - DROPDOWN */}
            <MultiSelectDropdown label="How do you help founders navigate investor bias? (Select all that apply)" options={['Reframing their narrative', 'Building a strong advisory board', 'Highlighting successful examples of their structure', 'Strengthening their support network', 'Improving their pitch', 'Targeting investors friendly to their structure', 'Advising them to adjust team structure', 'Helping demonstrate accountability structures', 'I do not specifically address this', 'Other']} selected={formData.navigatingBias} onChange={selected => setFormData({
            ...formData,
            navigatingBias: selected
          })} placeholder="Select strategies..." otherValue={formData.navigatingBiasOther} onOtherChange={value => setFormData({
            ...formData,
            navigatingBiasOther: value
          })} data-id="element-861" />

            {/* Advised Add Cofounder */}
            <div data-id="element-862">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-863">
                Have you ever advised a solo founder to bring on a cofounder?
              </label>
              <div className="space-y-2" data-id="element-864">
                {['Yes, frequently', 'Yes, occasionally', 'Rarely', 'Never'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-865">
                    <input type="radio" name="advisedAddCofounder" value={option} checked={formData.advisedAddCofounder === option} onChange={e => setFormData({
                  ...formData,
                  advisedAddCofounder: e.target.value
                })} className="w-4 h-4" data-id="element-866" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-867">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Add Cofounder Factors - DROPDOWN */}
            <MultiSelectDropdown label="What factors influence whether you recommend adding a cofounder? (Select all that apply)" options={['Critical skill gaps', 'Emotional struggles with isolation', 'Investor pressure', 'Scaling challenges', 'Burnout risk', "Founder's own preference and openness", 'I never recommend it—it is their decision', 'Other']} selected={formData.addCofounderFactors} onChange={selected => setFormData({
            ...formData,
            addCofounderFactors: selected
          })} placeholder="Select factors..." otherValue={formData.addCofounderFactorsOther} onOtherChange={value => setFormData({
            ...formData,
            addCofounderFactorsOther: value
          })} data-id="element-868" />
          </div>;
      case 'gaps':
        return <div className="space-y-8" data-id="element-869">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-870">
              Ecosystem Gaps
            </h3>

            {/* Resource Effectiveness - Likert */}
            <div data-id="element-871">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-872">
                On a scale of 1–5, how effective are current resources in
                supporting founders overall?
              </label>
              <p className="text-[#3D5654] mb-4 text-sm" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-873">
                1 = Not effective at all → 5 = Highly effective
              </p>
              <div className="flex gap-4 justify-center" data-id="element-874">
                {[1, 2, 3, 4, 5].map(num => <button key={num} onClick={() => setFormData({
                ...formData,
                resourceEffectiveness: num
              })} className={`w-14 h-14 rounded-full border-2 transition-all flex items-center justify-center ${formData.resourceEffectiveness === num ? 'bg-[#1A3D3A] text-white border-[#1A3D3A] scale-110' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40 hover:scale-105'}`} style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '20px',
                fontWeight: 600
              }} data-id="element-875">
                    {num}
                  </button>)}
              </div>
            </div>

            {/* Solo Support Gaps - DROPDOWN */}
            <MultiSelectDropdown label="What support gaps exist for solo founders? (Select all that apply)" options={['Peer communities specifically for solo founders', 'Mental health resources tailored to founders', 'Decision-making support tools', 'Affordable coaching options', 'Solo-founder-friendly investors', 'Structured accountability programs', 'Crisis support resources', 'Recognition of solo founder legitimacy', 'Research and data on solo founder outcomes', 'Skill-sharing or fractional support networks', 'Other']} selected={formData.soloSupportGaps} onChange={selected => setFormData({
            ...formData,
            soloSupportGaps: selected
          })} placeholder="Select gaps..." otherValue={formData.soloSupportGapsOther} onOtherChange={value => setFormData({
            ...formData,
            soloSupportGapsOther: value
          })} data-id="element-876" />

            {/* Multi Support Gaps - DROPDOWN */}
            <MultiSelectDropdown label="What support gaps exist for multi-founder teams? (Select all that apply)" options={['Cofounder relationship support', 'Conflict mediation services', 'Team coaching', 'Cofounder matching services', 'Operating agreement resources', 'Equity guidance', 'Communication training', 'Role clarity frameworks', 'Cofounder prenup templates', 'Breakup support and transition help', 'Other']} selected={formData.multiSupportGaps} onChange={selected => setFormData({
            ...formData,
            multiSupportGaps: selected
          })} placeholder="Select gaps..." otherValue={formData.multiSupportGapsOther} onOtherChange={value => setFormData({
            ...formData,
            multiSupportGapsOther: value
          })} data-id="element-877" />

            {/* Valuable Resources - DROPDOWN */}
            <MultiSelectDropdown label="Which resources would be most valuable for founding teams overall? (Select all that apply)" options={['Cofounder relationship assessment tools', 'Founder mental health resources', 'Decision-making frameworks', 'Conflict resolution training', 'Peer matching programs', 'Team dynamics coaching', 'Equity and legal templates', 'Accountability structures', 'Founder wellbeing programs', 'Investor education on team dynamics', 'Other']} selected={formData.valuableResources} onChange={selected => setFormData({
            ...formData,
            valuableResources: selected
          })} placeholder="Select resources..." otherValue={formData.valuableResourcesOther} onOtherChange={value => setFormData({
            ...formData,
            valuableResourcesOther: value
          })} data-id="element-878" />

            {/* Use Assessment Tool */}
            <div data-id="element-879">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-880">
                If a structured assessment tool existed to identify founder and
                team strengths and gaps, would you use it?
              </label>
              <div className="space-y-2" data-id="element-881">
                {['Yes, definitely', 'Yes, probably', 'Maybe', 'Probably not', 'Definitely not'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-882">
                    <input type="radio" name="useAssessmentTool" value={option} checked={formData.useAssessmentTool === option} onChange={e => setFormData({
                  ...formData,
                  useAssessmentTool: e.target.value
                })} className="w-4 h-4" data-id="element-883" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-884">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Tool Features - DROPDOWN */}
            <MultiSelectDropdown label="What features would make such a tool most useful? (Select all that apply)" options={['Identifies individual skill gaps', 'Assesses emotional resilience', 'Evaluates cofounder compatibility', 'Benchmarks against other founders', 'Provides actionable recommendations', 'Tracks progress over time', 'Integrates with coaching workflows', 'Generates reports for investors', 'Free or low-cost', 'Evidence-based methodology', 'Other']} selected={formData.toolFeatures} onChange={selected => setFormData({
            ...formData,
            toolFeatures: selected
          })} placeholder="Select features..." otherValue={formData.toolFeaturesOther} onOtherChange={value => setFormData({
            ...formData,
            toolFeaturesOther: value
          })} data-id="element-885" />
          </div>;
      case 'business':
        return <div className="space-y-8" data-id="element-886">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-887">
              Business Model
            </h3>

            {/* Compensation Model - DROPDOWN */}
            <MultiSelectDropdown label="What is your compensation model? (Select all that apply)" options={['Hourly rate', 'Monthly retainer', 'Equity only', 'Cash plus equity', 'Project-based fee', 'Free or pro bono', 'Sliding scale based on ability to pay', 'Success-based fee', 'Other']} selected={formData.compensationModel} onChange={selected => setFormData({
            ...formData,
            compensationModel: selected
          })} placeholder="Select models..." otherValue={formData.compensationModelOther} onOtherChange={value => setFormData({
            ...formData,
            compensationModelOther: value
          })} data-id="element-888" />

            {/* Rate Range */}
            <div data-id="element-889">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-890">
                What is your typical rate range?
              </label>
              <div className="space-y-2" data-id="element-891">
                {['Less than $100/hour or $500/month', '$100–250/hour or $500–1,500/month', '$250–500/hour or $1,500–5,000/month', 'More than $500/hour or $5,000/month', 'Equity only', 'Varies widely', 'Prefer not to say'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-892">
                    <input type="radio" name="rateRange" value={option} checked={formData.rateRange === option} onChange={e => setFormData({
                  ...formData,
                  rateRange: e.target.value
                })} className="w-4 h-4 flex-shrink-0" data-id="element-893" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-894">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Reduced Rates */}
            <div data-id="element-895">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-896">
                Do you offer reduced rates or pro bono work for early-stage
                founders?
              </label>
              <div className="space-y-2" data-id="element-897">
                {['Yes, regularly', 'Yes, occasionally', 'No, but I am open to it', 'No'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-898">
                    <input type="radio" name="reducedRates" value={option} checked={formData.reducedRates === option} onChange={e => setFormData({
                  ...formData,
                  reducedRates: e.target.value
                })} className="w-4 h-4" data-id="element-899" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-900">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>
          </div>;
      case 'reflections':
        return <div className="space-y-8" data-id="element-901">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-902">
              Final Reflections
            </h3>

            {/* Biggest Frustration */}
            <div data-id="element-903">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-904">
                What is your biggest frustration working with founders?
              </label>
              <textarea value={formData.biggestFrustration} onChange={e => setFormData({
              ...formData,
              biggestFrustration: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your thoughts..." data-id="element-905" />
            </div>

            {/* Biggest Reward */}
            <div data-id="element-906">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-907">
                What is your biggest reward working with founders?
              </label>
              <textarea value={formData.biggestReward} onChange={e => setFormData({
              ...formData,
              biggestReward: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your thoughts..." data-id="element-908" />
            </div>

            {/* Biggest Success Factor */}
            <div data-id="element-909">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-910">
                Based on your experience, what is the single biggest factor in
                founding team success?
              </label>
              <textarea value={formData.biggestSuccessFactor} onChange={e => setFormData({
              ...formData,
              biggestSuccessFactor: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your insights..." data-id="element-911" />
            </div>

            {/* Wish Founders Understood */}
            <div data-id="element-912">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-913">
                What do you wish all founders understood about team dynamics and
                partnerships before starting?
              </label>
              <textarea value={formData.wishFoundersUnderstood} onChange={e => setFormData({
              ...formData,
              wishFoundersUnderstood: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your wisdom..." data-id="element-914" />
            </div>

            {/* Success Story */}
            <div data-id="element-915">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-916">
                What is your most memorable success story involving founder or
                team dynamics? (No names needed)
              </label>
              <textarea value={formData.successStory} onChange={e => setFormData({
              ...formData,
              successStory: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share a success story..." data-id="element-917" />
            </div>

            {/* Cautionary Tale */}
            <div data-id="element-918">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-919">
                What is your most memorable cautionary tale involving founder or
                team dynamics? (No names needed)
              </label>
              <textarea value={formData.cautionaryTale} onChange={e => setFormData({
              ...formData,
              cautionaryTale: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share a cautionary tale..." data-id="element-920" />
            </div>

            {/* Advice to Coaches */}
            <div data-id="element-921">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-922">
                What advice would you give to other coaches or mentors working
                with founders?
              </label>
              <textarea value={formData.adviceToCoaches} onChange={e => setFormData({
              ...formData,
              adviceToCoaches: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share your advice..." data-id="element-923" />
            </div>

            {/* Anything Else */}
            <div data-id="element-924">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-925">
                Is there anything else you would like to share about your
                experience supporting founders?
              </label>
              <textarea value={formData.anythingElse} onChange={e => setFormData({
              ...formData,
              anythingElse: e.target.value
            })} rows={4} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none resize-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Share any additional thoughts..." data-id="element-926" />
            </div>
          </div>;
      case 'closing':
        return <div className="space-y-8" data-id="element-927">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-928">
              Closing
            </h3>

            {/* Open to Follow Up */}
            <div data-id="element-929">
              <label className="block text-[#1A3D3A] mb-3" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '16px',
              fontWeight: 500
            }} data-id="element-930">
                Would you be open to a follow-up conversation to discuss your
                experiences in more depth?
              </label>
              <div className="space-y-2" data-id="element-931">
                {['Yes', 'No', 'Maybe'].map(option => <label key={option} className="flex items-center gap-3 p-3 border-2 border-[#1A3D3A]/20 rounded-lg hover:border-[#1A3D3A]/40 cursor-pointer transition-colors" data-id="element-932">
                    <input type="radio" name="openToFollowUp" value={option} checked={formData.openToFollowUp === option} onChange={e => setFormData({
                  ...formData,
                  openToFollowUp: e.target.value
                })} className="w-4 h-4" data-id="element-933" />
                    <span style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: '15px'
                }} data-id="element-934">
                      {option}
                    </span>
                  </label>)}
              </div>
            </div>

            {/* Thank You Message */}
            <div className="bg-[#F8FAF9] p-6 rounded-lg" data-id="element-935">
              <p className="text-[#3D5654]" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              lineHeight: 1.7
            }} data-id="element-936">
                Thank you for completing this survey! Your insights are
                invaluable to understanding founder dynamics and improving
                support for entrepreneurs.
              </p>
            </div>
          </div>;
      default:
        return <div className="space-y-6" data-id="element-937">
            <p className="text-[#3D5654]" style={{
            fontFamily: "'DM Sans', sans-serif"
          }} data-id="element-938">
              Section content for {currentSection} - Coming soon
            </p>
          </div>;
    }
  };
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto" data-id="element-939">
      <div className="min-h-screen flex items-start justify-center p-4 py-8" data-id="element-940">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-[16px] w-full max-w-4xl relative" data-id="element-941">
          {/* Header */}
          <div className="p-6 border-b border-[#1A3D3A]/10" data-id="element-942">
            <div className="flex justify-between items-start mb-4" data-id="element-943">
              <div data-id="element-944">
                <h2 className="text-[#1A3D3A] mb-2" style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: '28px',
                fontWeight: 500
              }} data-id="element-945">
                  Mentor & Advisor Research
                </h2>
                <p className="text-[#3D5654] text-sm" style={{
                fontFamily: "'DM Sans', sans-serif"
              }} data-id="element-946">
                  {completedSections.size} of {SECTIONS.length} sections
                  completed
                </p>
              </div>
              <button onClick={onClose} className="text-[#3D5654] hover:text-[#1A3D3A] transition-colors" data-id="element-947">
                <X className="w-6 h-6" data-id="element-948" />
              </button>
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-id="element-949">
              {SECTIONS.map(section => <button key={section.id} onClick={() => handleSectionChange(section.id)} className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm transition-all flex items-center gap-2 ${currentSection === section.id ? 'bg-[#1A3D3A] text-white' : completedSections.has(section.id) ? 'bg-[#C5F467] text-[#1A3D3A]' : 'bg-[#F8FAF9] text-[#3D5654] hover:bg-[#E8EAE9]'}`} style={{
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 500
            }} data-id="element-950">
                  {completedSections.has(section.id) && <Check className="w-4 h-4" data-id="element-951" />}
                  {section.label}
                  {section.required && <span className="text-xs opacity-60" data-id="element-952">*</span>}
                </button>)}
            </div>
          </div>

          {/* Content */}
          <div className="p-8" data-id="element-953">
            <AnimatePresence mode="wait" data-id="element-954">
              <motion.div key={currentSection} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.2
            }} data-id="element-955">
                {renderSection()}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation Footer */}
          <div className="p-6 border-t border-[#1A3D3A]/10 flex justify-between items-center" data-id="element-956">
            <div className="text-sm text-[#3D5654]" style={{
            fontFamily: "'DM Sans', sans-serif"
          }} data-id="element-957">
              {SECTIONS.find(s => s.id === currentSection)?.required && <span data-id="element-958">* Required section</span>}
              {isSaving && <span className="text-[#1A3D3A] ml-4" data-id="element-959">💾 Saving...</span>}
            </div>
            <button onClick={currentSection === 'closing' ? handleSubmit : goToNextSection} disabled={isSubmitting || isSaving} className="flex items-center gap-2 px-8 py-3 bg-[#1A3D3A] text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50" style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '14px',
            fontWeight: 600
          }} data-id="element-960">
              {isSubmitting ? 'Submitting...' : isSaving ? 'Saving...' : currentSection === 'closing' ? 'Submit Survey' : <>
                  Continue
                  <ChevronRight className="w-4 h-4" data-id="element-961" />
                </>}
            </button>
          </div>
        </motion.div>
      </div>
    </div>;
}